// src/utils/axiosConfig.js
import axios from 'axios';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api',
  withCredentials: true, // CRITICAL: Allow cookies (for refresh token)
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 10000 // 10 second timeout
});

// Store for managing refresh token requests
let isRefreshing = false;
let failedQueue = [];

// Process queued requests after token refresh
const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * REQUEST INTERCEPTOR
 * Automatically attach access token to all requests
 */
api.interceptors.request.use(
  (config) => {
    // Get access token from window object (set by AuthContext)
    const token = window.accessToken;
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    console.log(`API Request: ${config.method.toUpperCase()} ${config.url}`, {
      hasToken: !!token,
      baseURL: config.baseURL
    });
    
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

/**
 * RESPONSE INTERCEPTOR
 * Automatically refresh token on 401 errors
 */
api.interceptors.response.use(
  (response) => {
    // Success response - just return data
    console.log(`API Response: ${response.config.method.toUpperCase()} ${response.config.url}`, {
      status: response.status,
      success: response.data?.success
    });
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    console.error('API Error:', {
      url: originalRequest?.url,
      status: error.response?.status,
      code: error.response?.data?.code,
      message: error.response?.data?.message
    });

    // If error is not 401 or request already retried, reject immediately
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // Check if it's a TOKEN_EXPIRED error
    if (error.response?.data?.code === 'TOKEN_EXPIRED') {
      console.log('Token expired, attempting refresh...');

      // If already refreshing, queue this request
      if (isRefreshing) {
        console.log('Refresh in progress, queueing request...');
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      // Mark as retrying
      originalRequest._retry = true;
      isRefreshing = true;

      try {
        console.log('Calling refresh token endpoint...');
        
        // Call refresh token endpoint
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api'}/users/refresh`,
          {},
          { 
            withCredentials: true,
            timeout: 5000
          }
        );

        if (response.data.success && response.data.accessToken) {
          const newAccessToken = response.data.accessToken;
          
          console.log('Token refresh successful');
          
          // Update token in memory
          window.accessToken = newAccessToken;
          
          // Trigger custom event to update AuthContext
          window.dispatchEvent(
            new CustomEvent('tokenRefreshed', { 
              detail: { 
                token: newAccessToken,
                user: response.data.user 
              } 
            })
          );

          // Process queued requests with new token
          processQueue(null, newAccessToken);

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return api(originalRequest);
        } else {
          throw new Error('Refresh failed - no token received');
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        
        // Process queue with error
        processQueue(refreshError, null);
        
        // Clear token and trigger logout
        window.accessToken = null;
        window.dispatchEvent(new CustomEvent('authError', { 
          detail: { message: 'Session expired. Please login again.' }
        }));
        
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // For other 401 errors (invalid token, no token, etc.)
    if (error.response?.data?.code === 'NO_TOKEN' || 
        error.response?.data?.code === 'INVALID_TOKEN') {
      console.log('Authentication required');
      
      // Trigger auth error event
      window.dispatchEvent(new CustomEvent('authError', { 
        detail: { message: error.response.data.message }
      }));
    }

    return Promise.reject(error);
  }
);

// Export the configured instance as default AND named export
export default api;
export { api }; // Add this line to also export as named export